import type { FeedbackCategory } from "@/types";

/**
 * The URL state of `/test-cases/all` (fe/TEST_CASES_ALL.md §5, hub §4.2).
 * Filters, sort and the open drawer all live here — there is no `useState`
 * for any of them, which is what makes a filtered row open a shareable link.
 *
 * This is normalization, not validation: a pasted link, an old bookmark or a
 * hand-edited address must land on a sane default, never on a thrown error.
 */

export type AllTestCasesSort = "promoted" | "tc" | "feedback";
export type AllTestCasesStateFilter = "all" | "active" | "retired";

export type AllTestCasesSearch = {
  /** Free text over the trigger body and the TC number. */
  q: string;
  /** Feedback-category chips, OR-matched against the snapshot column. */
  cats: FeedbackCategory[];
  /**
   * Domain chips, OR-matched against `eval_test_case.domain`. `UNCLASSIFIED`
   * stands for the null column — it has to be selectable, or the TCs promoted
   * before the classifier are the one group the filter cannot name.
   */
  doms: string[];
  state: AllTestCasesStateFilter;
  sort: AllTestCasesSort;
  /** The TC whose drawer is open; null when the drawer is closed. */
  tc: string | null;
};

export const ALL_TEST_CASES_DEFAULT_SEARCH: AllTestCasesSearch = {
  q: "",
  cats: [],
  doms: [],
  state: "all",
  sort: "promoted",
  tc: null,
};

type Option<T extends string> = { readonly value: T; readonly label: string };

/**
 * The two toolbar dropdowns, as data.
 *
 * One list per axis rather than three copies of the same literals: the values
 * the parser accepts, the options the popup renders and the label the
 * collapsed trigger prints all read from here. Base UI's `<Select.Value/>`
 * shows the raw value unless the root is handed this list — so a hand-written
 * option that is not in it prints `retired` where it means 은퇴만.
 */
export const STATE_FILTER_OPTIONS: readonly Option<AllTestCasesStateFilter>[] = [
  { value: "all", label: "활성 · 은퇴 전체" },
  { value: "active", label: "활성만" },
  { value: "retired", label: "은퇴만" },
];

export const SORT_OPTIONS: readonly Option<AllTestCasesSort>[] = [
  { value: "promoted", label: "최근 승격순" },
  { value: "tc", label: "TC 번호순" },
  { value: "feedback", label: "👎 많은 순" },
];

const STATE_FILTERS = STATE_FILTER_OPTIONS.map((option) => option.value);
const SORTS = SORT_OPTIONS.map((option) => option.value);

/** `onValueChange` hands back `unknown`; these are what narrow it. */
export const isStateFilter = (value: unknown): value is AllTestCasesStateFilter =>
  isOneOf(value, STATE_FILTERS) !== null;

export const isSort = (value: unknown): value is AllTestCasesSort =>
  isOneOf(value, SORTS) !== null;

/** The 👎 chips offered today (BE `NEGATIVE_CATEGORIES`, 10 terms). */
export const OFFERED_CATEGORIES: readonly FeedbackCategory[] = [
  "factual_error",
  "incomplete",
  "mention_wrong",
  "citation_missing",
  "citation_wrong",
  "off_target",
  "tool_misuse",
  "style_or_tone",
  "safety_or_legal",
  "other",
];

/**
 * No longer offered, still in stored rows. `slow_or_buggy` ("느리거나") was
 * retired with rubric v2: response time is measured from the run record by
 * its Execution group, and asking the user too would count one delay twice
 * (BE `61dd4851`).
 */
export const RETIRED_CATEGORIES: readonly FeedbackCategory[] = ["slow_or_buggy"];

/**
 * Everything a stored row can carry — BE `STORED_NEGATIVE_CATEGORIES`. The
 * filter and the URL take all of it, or past rows would become unfindable;
 * retired terms go last so the chip row stays stable as pages load in.
 */
export const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = [
  ...OFFERED_CATEGORIES,
  ...RETIRED_CATEGORIES,
];

export const isRetiredCategory = (slug: FeedbackCategory) => RETIRED_CATEGORIES.includes(slug);

/**
 * The domains `rubric_v2.json` has Category criteria for, in its own order
 * (BE `app/assets/eval/rubric_v2.json`). These are the only ones that score.
 *
 * NOT a closed vocabulary for the column: the classifier may store a domain
 * that is not here, and that proposal is the signal for whether criteria are
 * worth writing (be/RUBRIC.md R9.2). So this list orders and groups the
 * chips; it never filters a stored value out of the screen.
 */
export const RUBRIC_DOMAINS: readonly string[] = [
  "location_search",
  "shopping",
  "current_events",
  "finance",
  "general_conversation",
];

/** Korean for the five scored domains. A proposal prints its raw slug. */
export const DOMAIN_LABELS: Readonly<Record<string, string>> = {
  location_search: "장소 검색",
  shopping: "쇼핑",
  current_events: "시사",
  finance: "금융",
  general_conversation: "일상 대화",
};

/**
 * The filter value for `domain IS NULL`. A reserved word rather than an empty
 * string: `?doms=` with no value is an absent filter, and the two have to be
 * distinguishable in a link.
 */
export const UNCLASSIFIED_DOMAIN = "__none__" as const;

export const domainLabel = (slug: string) =>
  slug === UNCLASSIFIED_DOMAIN ? "미분류" : (DOMAIN_LABELS[slug] ?? slug);

/** A stored domain the rubric has no criteria for — an unapproved proposal. */
export const isProposedDomain = (slug: string) =>
  slug !== UNCLASSIFIED_DOMAIN && !RUBRIC_DOMAINS.includes(slug);

export function parseAllTestCasesSearch(raw: Record<string, unknown>): AllTestCasesSearch {
  return {
    q: asSearchText(raw.q),
    cats: asCategories(raw.cats),
    doms: asDomains(raw.doms),
    state: isOneOf(raw.state, STATE_FILTERS) ?? "all",
    sort: isOneOf(raw.sort, SORTS) ?? "promoted",
    tc: asSingle(raw.tc),
  };
}

function isOneOf<T extends string>(value: unknown, set: readonly T[]): T | null {
  return typeof value === "string" && (set as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function asSearchText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

/**
 * `?cats=a&cats=b` arrives as an array, `?cats=a` as a string. Unknown slugs
 * are dropped rather than kept — a kept slug filters to a quiet zero.
 */
function asCategories(value: unknown): FeedbackCategory[] {
  const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  const out: FeedbackCategory[] = [];
  for (const entry of values) {
    if (
      typeof entry === "string" &&
      (FEEDBACK_CATEGORIES as readonly string[]).includes(entry) &&
      !out.includes(entry as FeedbackCategory)
    ) {
      out.push(entry as FeedbackCategory);
    }
  }
  return out;
}

/**
 * Unlike `cats`, an unknown value is KEPT. The domain set is open, so the
 * client has no list to validate against — dropping a slug it does not
 * recognise would break exactly the links that filter on a newly proposed
 * domain. Shape is all that is checked.
 */
function asDomains(value: unknown): string[] {
  const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  const out: string[] = [];
  for (const entry of values) {
    if (typeof entry !== "string") continue;
    const slug = entry.trim();
    if (!slug || slug.length > 48 || out.includes(slug)) continue;
    out.push(slug);
  }
  return out;
}

/**
 * The drawer holds one TC. A repeated or non-string `tc` is treated as
 * absent — picking the first of two would open a row the user did not ask
 * for.
 */
function asSingle(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * The route id, which equals the URL. Kept beside the search shape so the
 * page's `useSearch`/`navigate` calls and the route definition cannot drift
 * apart — and so the page does not import the route module, which imports it.
 */
export const ALL_TEST_CASES_ROUTE = "/test-cases/all" as const;
